import { motion } from "framer-motion";

import { Section, SectionHead, Card, ArrowLink, Chip } from "../components/ui";
import { Reveal } from "../components/Reveal";
import { Spine } from "../components/Spine";
import { PILLARS } from "../data/capabilities";
import { PRODUCTS } from "../data/products";
import { SmartLink } from "../components/SmartLink";

/**
 * The three engagements — laid out asymmetrically on purpose.
 *
 * Consult and Build share a row; Product takes a full-width block below
 * them. That is not decoration, it is the argument: the products are the
 * span that reaches all the way to Operate, and giving them the same
 * 1/3 column as the other two would quietly say they are just a third
 * service line.
 *
 * Each block carries its own small diagram rather than an icon. An icon
 * says "this is a card"; a diagram says something about the work.
 */

function DecisionMotif() {
  return (
    <svg viewBox="0 0 120 56" className="h-14 w-28" aria-hidden fill="none">
      <path d="M6 28h22" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" />
      <path
        d="M28 28c14 0 14-20 28-20M28 28h28M28 28c14 0 14 20 28 20"
        stroke="rgba(255,255,255,0.16)"
        strokeWidth="1.5"
      />
      <motion.path
        d="M56 28h34"
        stroke="url(#dm)"
        strokeWidth="2"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, delay: 0.3 }}
      />
      <circle cx="6" cy="28" r="3" fill="rgba(255,255,255,0.45)" />
      <circle cx="56" cy="8" r="2.5" fill="rgba(255,255,255,0.18)" />
      <circle cx="56" cy="48" r="2.5" fill="rgba(255,255,255,0.18)" />
      <circle cx="92" cy="28" r="4" fill="#10b981" />
      <defs>
        <linearGradient id="dm" x1="56" y1="28" x2="92" y2="28">
          <stop stopColor="#10b981" stopOpacity="0.4" />
          <stop offset="1" stopColor="#10b981" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function LayersMotif() {
  return (
    <svg viewBox="0 0 120 56" className="h-14 w-28" aria-hidden fill="none">
      {[0, 1, 2, 3].map((i) => (
        <motion.rect
          key={i}
          x={18 + i * 3}
          y={44 - i * 11}
          width={84 - i * 6}
          height="8"
          rx="2"
          fill={i === 3 ? "rgba(139,92,246,0.85)" : "rgba(255,255,255,0.10)"}
          stroke="rgba(255,255,255,0.16)"
          strokeWidth="1"
          initial={{ opacity: 0, y: 6 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.35, delay: 0.15 + i * 0.08 }}
        />
      ))}
    </svg>
  );
}

export function Pillars() {
  const [consult, build, product] = PILLARS;

  return (
    <Section id="what-we-do">
      <div className="container">
        <SectionHead
          eyebrow="What we do"
          title={
            <>
              From technology strategy to{" "}
              <span className="text-gradient">software that ships.</span>
            </>
          }
          lede="One delivery line, three places to join it. Most companies come in at one point and stay there; some walk the whole thing with us."
        />

        <Reveal delay={0.1} className="mt-10 sm:mt-14">
          <Card className="p-5 sm:p-8">
            <Spine />
          </Card>
        </Reveal>

        {/* Consult + Build */}
        <div className="mt-5 grid gap-5 md:grid-cols-2">
          {[consult, build].map((p, i) => (
            <Reveal key={p.key} delay={i * 0.08}>
              <Card
                interactive
                accent={i === 0 ? "emerald" : "violet"}
                className="flex h-full flex-col p-6 sm:p-8"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/40">
                      {p.short}
                    </div>
                    <h3 className="mt-1.5 font-display text-xl font-bold tracking-tight sm:text-2xl">
                      {p.title}
                    </h3>
                  </div>
                  <div className="shrink-0 opacity-80">
                    {i === 0 ? <DecisionMotif /> : <LayersMotif />}
                  </div>
                </div>

                <p className="mt-3 text-sm leading-relaxed text-white/65">{p.lede}</p>

                <div className="mt-5 flex flex-wrap gap-1.5">
                  {p.capabilities.slice(0, 6).map((c) => (
                    <Chip key={c}>{c}</Chip>
                  ))}
                  {p.capabilities.length > 6 ? (
                    <span className="inline-flex items-center px-1 text-xs text-white/35">
                      +{p.capabilities.length - 6} more
                    </span>
                  ) : null}
                </div>

                <div className="mt-auto pt-6">
                  <ArrowLink href={p.href}>{p.cta}</ArrowLink>
                </div>
              </Card>
            </Reveal>
          ))}
        </div>

        {/* Product — full width, because it is the part that is different */}
        <Reveal delay={0.16} className="mt-5">
          <Card interactive accent="emerald" className="overflow-hidden">
            <div className="grid gap-6 p-6 sm:p-8 lg:grid-cols-[1fr_1.1fr] lg:items-center lg:gap-10">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/40">
                  {product.short}
                </div>
                <h3 className="mt-1.5 font-display text-xl font-bold tracking-tight sm:text-2xl">
                  {product.title}
                </h3>
                <p className="mt-3 max-w-lg text-sm leading-relaxed text-white/65">
                  {product.lede}
                </p>
                <div className="mt-6">
                  <ArrowLink href={product.href}>{product.cta}</ArrowLink>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {PRODUCTS.map((prod) => (
                  <SmartLink
                    key={prod.slug}
                    href={prod.href}
                    className="group/p rounded-xl border border-white/10 bg-white/[0.03] p-4 transition-colors hover:border-white/25 hover:bg-white/[0.06]"
                  >
                    <div className="flex items-center gap-2">
                      <span className="size-1.5 rounded-full bg-emerald-400" />
                      <span className="text-sm font-semibold text-white">
                        {prod.name}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-white/50">{prod.tagline}</div>
                    <div className="mt-3 text-[11px] leading-relaxed text-white/40">
                      {prod.presence}
                    </div>
                  </SmartLink>
                ))}
              </div>
            </div>
          </Card>
        </Reveal>
      </div>
    </Section>
  );
}
